'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { Card, useMediaQuery } from '@mui/material'
import { AuctionDisplay } from '@components/AuctionDisplay'
import Image, { StaticImageData } from 'next/image'
import { redirect, useRouter } from 'next/navigation'
import AuctionActions from './components/AuctionActions'
import useAuctionManager, {
  AuctionManagerProvider,
} from '@hooks/useAuctionManager'
import { toNumber } from '@helium/spl-utils'
import { isPast } from 'date-fns'
import DateHeader from './components/DateHeader'
import { IdlAccounts } from '@coral-xyz/anchor'
import { AuctionManager } from '@dewicats/idls/lib/types/auction_manager'
import { useAsync } from 'react-async-hook'
import { FastAverageColor } from 'fast-average-color'
import dynamic from 'next/dynamic'
import AuctionBids from './components/AuctionBids'
import OpenLinkIcon from '@assets/openLinkIcon.svg'
import { useConnection } from '@solana/wallet-adapter-react'
import { CollectionProvider } from '@hooks/useCollection'
import { useMint2022 } from '@hooks/useOwnedAmount2022'
import CancelBidsBanner from '@components/CancelBidsBanner'
import Crown from '@assets/crown.svg'
import PoweredByWhite from '@assets/powered-by-white.svg'
import StakingPool from './components/StakingPool'
import CatIcon from '@assets/myCatsIcon.svg'
import ReferralPool from './components/ReferralPool'
import TeamDay from './components/TeamDay'
import FloatingDabbaButton from '@components/FloatingDabbaButton'
import { NATIVE_MINT } from '@solana/spl-token'
import FloatingWayruButton from '@components/FloatingWayruButton'
import StakingPower from './components/StakingPower'

const AuctionBody = dynamic(() => import('./components/AuctionBody'), {
  ssr: false,
})

export type DewiCard = {
  name: string
  attributes: {
    value: string
    type: string
  }[]
  decimals: number
  soldPrice?: number
  highestBidder?: IdlAccounts<AuctionManager>['bidRecieptV0']
}

const CardRoot = styled(Card)({
  background: 'var(--dewi_colors_dark_6) !important',
  backgroundColor: 'var(--dewi_colors_dark_9) !important',
  border: '1px solid var(--dewi_colors_dark_6)',
  borderRadius: 'var(--dewi_radius_xl)',
  display: 'flex',
  flexDirection: 'row',
  height: 'fit-content',
  flexWrap: 'wrap',
  gap: 'var(--dewi_spacing_20) 0',
  margin: 'var(--dewi_spacing_0) var(--dewi_spacing_4)',
})

const MEOW_HREF = '/meow/'

const Auction = ({ page }: { page: number }) => {
  const {
    bids,
    highestBidder,
    currentListing,
    getListings,
    allCats,
    currentCat,
    metadata,
    name: nftName,
    image,
  } = useAuctionManager()
  const router = useRouter()
  const [auctionEnded, setAuctionEnded] = useState(undefined)

  const [calculatedColor, setCalculatedColor] = useState<string | undefined>()

  const isLegendary = useMemo(
    () =>
      metadata?.find(
        ({ trait_type }: { trait_type: string }) =>
          trait_type?.toLowerCase() === 'aura',
      ),
    [metadata],
  )

  const isStakeTypeListing = useMemo(
    () => currentListing?.rewardType?.stake !== undefined,
    [currentListing],
  )

  const isReferralTypeListing = useMemo(
    () => currentListing?.rewardType?.referral !== undefined,
    [currentListing],
  )

  const isTeamDayTypeListing = useMemo(
    () => currentListing?.rewardPercentage?.toNumber() === 0,
    [currentListing],
  )

  useEffect(() => {
    getListings()
  }, [getListings])

  useEffect(() => {
    if (!currentListing) return

    const auctionEndDate = new Date(currentListing?.endAt.toNumber() * 1000)
    // check if highest bidder date was at least 1 min ago and auction has ended
    setAuctionEnded(isPast(auctionEndDate))
  }, [highestBidder, currentListing])

  const realPageIndex = useMemo(() => {
    if (!allCats?.length && page === undefined) return 1
    return page === undefined ? allCats?.length : page
  }, [allCats, page])

  const handleDateChange = useCallback(
    (change: 'prev' | 'next') => {
      switch (change) {
        case 'next':
          router.replace(`${MEOW_HREF}${realPageIndex + 1}`, {
            scroll: false,
          })
          break
        case 'prev':
          router.replace(`${MEOW_HREF}${realPageIndex - 1}`, {
            scroll: false,
          })
          break
      }
    },
    [realPageIndex, router],
  )

  const handleDisableNextButton = useCallback(
    () => realPageIndex - 1 === allCats?.length - 1,
    [allCats?.length, realPageIndex],
  )

  const handleDisablePrevButton = useCallback(
    () => realPageIndex - 1 < 1,
    [realPageIndex],
  )

  const { info } = useMint2022(
    currentListing?.tokenMint?.toBase58() === NATIVE_MINT.toBase58()
      ? undefined
      : currentListing?.tokenMint,
  )

  const currentDewiCat = useMemo(() => {
    return {
      name: nftName,
      attributes: metadata?.attributes,
      decimals:
        currentListing?.tokenMint?.toBase58() === NATIVE_MINT.toBase58()
          ? 9
          : info?.decimals || 6,
      soldPrice: !auctionEnded
        ? undefined
        : toNumber(
            highestBidder?.amount,
            currentListing?.tokenMint?.toBase58() === NATIVE_MINT.toBase58()
              ? 9
              : info?.decimals || 6,
          ),
      highestBidder: highestBidder,
    }
  }, [nftName, metadata, currentListing, info, auctionEnded, highestBidder])

  const currentBid = useMemo(() => {
    if (!bids || bids?.length === 0) return undefined
    return toNumber(
      highestBidder.amount,
      currentListing?.tokenMint?.toBase58() === NATIVE_MINT.toBase58()
        ? 9
        : info?.decimals || 6,
    )
  }, [bids, highestBidder?.amount, info, currentListing?.tokenMint])

  const ShowAuctionActions = useCallback(() => {
    if (!currentListing && !currentCat) {
      return <h2 className="w-32 h-12 rounded bg-dark-7 animate-pulse my-4" />
    }

    if (!currentListing && currentCat) {
      return null
    }

    return (
      <AuctionActions currentBid={currentBid} currentDewiCat={currentDewiCat} />
    )
  }, [currentBid, currentDewiCat, currentListing, currentCat])

  useAsync(async () => {
    if (!image) return
    try {
      const fac = new FastAverageColor()

      const color = await fac.getColorAsync(image)
      const newColor = color.rgba.replace(/[^,]+(?=\))/, '0.4')

      setCalculatedColor(newColor)
    } catch (err) {
      console.error(err)
    }
  }, [image])

  const { connection } = useConnection()

  const isDevnet = useMemo(() => {
    return connection.rpcEndpoint.includes('devnet')
  }, [connection?.rpcEndpoint])

  const MeowTypeBadge = useCallback(() => {
    return (
      <>
        {isLegendary && (
          <div className="flex bg-yellow-500 border-[1px] border-dark-6 rounded-xs gap-2 text-dark-9 text-xs font-semibold p-2 items-center">
            <Crown color="var(--dewi_colors_yellow_500)" />
            Legendary
          </div>
        )}
        {!isLegendary && (
          <div className="flex bg-dark-7 border-[1px] border-dark-6 rounded-xs gap-2 text-white font-semibold p-2 items-center">
            <CatIcon color="white" width={18} height={18} />
            Normal
          </div>
        )}
      </>
    )
  }, [isLegendary])

  const openExplorerLink = useCallback(() => {
    window.open(
      `https://explorer.solana.com/address/${currentListing.publicKey.toBase58()}${isDevnet ? '?cluster=devnet' : ''}`,
      '_blank',
    )
  }, [currentListing?.publicKey, isDevnet])

  const isMobile = useMediaQuery('(max-width: 1024px)')

  if (
    (allCats?.length > 1 && realPageIndex - 1 >= allCats?.length) ||
    realPageIndex < 1
  ) {
    redirect('/')
  }

  return (
    <CardRoot
      className="shadow-custom"
      sx={{
        gap: isMobile ? '0 !important' : 'var(--dewi_spacing_20) 0',
      }}
      style={
        {
          '--shadow-color': calculatedColor,
        } as React.CSSProperties
      }
    >
      {isMobile && (
        <div className="flex flex-col px-6 pt-6 pb-2 gap-2 w-full items-center">
          <MeowTypeBadge />
          {nftName ? (
            <h2 className="font-space text-size-6 lg:text-size-8 font-bold">
              {nftName}
            </h2>
          ) : (
            <h2 className="w-32 h-12 rounded bg-dark-7 animate-pulse my-4" />
          )}
        </div>
      )}
      <AuctionDisplay traitsArray={metadata} image={image} />
      <div className="flex flex-col gap-3 flex-1 justify-start xs:pt-2 py-8 items-start lg:pr-12 lg:pt-14 xs:items-center lg:items-start px-6">
        {!isMobile && (
          <>
            <MeowTypeBadge />

            <div className="flex items-center gap-2 w-full">
              {nftName ? (
                <span className="font-space text-size-8 font-bold">
                  {nftName}
                  <span className="text-size-2 text-gray-6">/459</span>
                </span>
              ) : (
                <h2 className="w-32 h-12 rounded bg-dark-7 animate-pulse my-4" />
              )}
              <button
                onClick={openExplorerLink}
                className="hover:text-gray-1 text-gray-6"
              >
                <OpenLinkIcon
                  style={{
                    width: '32px',
                    height: '32px',
                    color: 'inherit',
                  }}
                />
              </button>
              <div className="flex flex-grow justify-end">
                <PoweredByWhite
                  width={180}
                  height={84.71}
                  className="object-contain"
                  alt="powered-by-white"
                  loading="lazy"
                  decoding="async"
                  data-nimg="fill"
                />
              </div>
            </div>
          </>
        )}
        <div className="flex flex-col p-1 rounded-md bg-dark-8 border-[1px] border-dark-7 w-full">
          <div className="flex mt-1 ml-1">
            <DateHeader
              dateChange={handleDateChange}
              disabledNext={handleDisableNextButton()}
              disabledPrev={handleDisablePrevButton()}
            />
            {auctionEnded === false && (
              <div className="flex gap-2 items-center text-xs font-medium text-green-500 mr-4">
                <span className="w-2 h-2 block rounded-sm bg-green-500 shadow-[0px_0px_4px_0px_#23E766]" />
                Live
              </div>
            )}
            {auctionEnded && (
              <div className="flex gap-2 items-center text-xs font-medium text-red-500 mr-4">
                <span className="w-2 h-2 block rounded-sm bg-red-500 shadow-[0px_0px_4px_0px_#ef4444]" />
                Ended
              </div>
            )}
            {auctionEnded === undefined && (
              <div className="flex gap-2 items-center justify-center mr-2">
                <span className="w-16 h-4 mx-auto rounded bg-dark-7 animate-pulse text-xs" />
              </div>
            )}
          </div>
          <div className="flex my-1 mt-2 mx-1 flex-col bg-dark-7 border-[1px] border-dark-6 rounded-md">
            <AuctionBody
              currentBid={currentBid}
              currentDewiCat={currentDewiCat}
            />
            <ShowAuctionActions />
            {isStakeTypeListing && !isTeamDayTypeListing && (
              <>
                <StakingPool currentBid={currentBid} />
                <StakingPower
                  stakingAmount={currentListing.totalStakedCount.toNumber()}
                />
              </>
            )}
            {isReferralTypeListing && !isTeamDayTypeListing && (
              <ReferralPool currentBid={currentBid} />
            )}
            {isTeamDayTypeListing && <TeamDay />}
          </div>
        </div>
        <AuctionBids currentDewiCat={currentDewiCat} />
      </div>
    </CardRoot>
  )
}

const AuctionManagerWrapper = ({ page }: { page?: number }) => {
  return (
    <CollectionProvider>
      <AuctionManagerProvider page={page}>
        <div className="relative">
          <Auction page={page} />
          <CancelBidsBanner />
          <FloatingDabbaButton />
          <FloatingWayruButton />
        </div>
      </AuctionManagerProvider>
    </CollectionProvider>
  )
}

export default AuctionManagerWrapper
